import { PROFILE_PHOTO_MAX_BYTES, PROFILE_PHOTO_SIDE } from "@teamna/shared";
import type { Square } from "./face-boxes.ts";

/**
 * The browser side of profile photos: read the file a person picks, and turn one square of it into
 * the 512 x 512 JPEG the API takes. A new JPEG drops everything the camera wrote in the file (the
 * place, the time, the phone), because a canvas keeps pixels only.
 */

export const ACCEPTED_PHOTO_TYPES = "image/jpeg,image/png,image/webp,image/heic,image/heif";
/** The largest file a person can pick. What we send is much smaller. */
export const PHOTO_FILE_MAX_BYTES = 25 * 1024 * 1024;
/** Larger photos are scaled down to this on the long side before any work. */
const WORK_MAX_SIDE = 3072;

export class PhotoFileError extends Error {}

/** A picked photo, scaled to the work size. All squares and boxes are in its pixels. */
export type PhotoSource = {
  fileName: string;
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /**
   * An object URL of the picked file, to show it on screen. The caller revokes it with
   * `releasePhoto` when it is done.
   */
  previewUrl: string;
};

/** Frees the memory of the preview. */
export function releasePhoto(source: PhotoSource | null): void {
  if (source?.previewUrl.startsWith("blob:")) URL.revokeObjectURL(source.previewUrl);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new PhotoFileError("We cannot open this photo. Save it as a JPG and try again."));
    image.src = url;
  });
}

/** Reads the picked file into a canvas. Throws a `PhotoFileError` with words for the person. */
export async function readPhotoFile(file: File): Promise<PhotoSource> {
  if (file.type && !file.type.startsWith("image/")) throw new PhotoFileError("Choose a photo: a JPG, PNG or WebP file.");
  if (file.size > PHOTO_FILE_MAX_BYTES) {
    throw new PhotoFileError(`This file is ${(file.size / 1048576).toFixed(1)} MB. Choose a photo of 25 MB or less.`);
  }
  const url = URL.createObjectURL(file);
  let kept = false;
  try {
    // The browser turns the photo the right way up from its camera data (image-orientation).
    const image = await loadImage(url);
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) throw new PhotoFileError("This photo is empty. Choose another photo.");
    const shrink = Math.min(1, WORK_MAX_SIDE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * shrink);
    canvas.height = Math.round(height * shrink);
    const context = canvas.getContext("2d");
    if (!context) throw new PhotoFileError("This browser cannot read photos. Try another browser.");
    context.imageSmoothingQuality = "high";
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    kept = true;
    return { fileName: file.name, canvas, width: canvas.width, height: canvas.height, previewUrl: url };
  } finally {
    if (!kept) URL.revokeObjectURL(url);
  }
}

function base64Of(dataUrl: string): string {
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

function bytesOf(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return (base64.length * 3) / 4 - padding;
}

export type PhotoCrop = { dataUrl: string; base64: string };

/** One square of the photo as the JPEG the API takes: 512 x 512, under the size limit. */
export function cropToJpeg(source: PhotoSource, square: Square): PhotoCrop {
  const canvas = document.createElement("canvas");
  canvas.width = PROFILE_PHOTO_SIDE;
  canvas.height = PROFILE_PHOTO_SIDE;
  const context = canvas.getContext("2d");
  if (!context) throw new PhotoFileError("This browser cannot make photos. Try another browser.");
  context.imageSmoothingQuality = "high";
  // A white floor, so a transparent PNG does not turn black in the JPEG.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, PROFILE_PHOTO_SIDE, PROFILE_PHOTO_SIDE);
  context.drawImage(source.canvas, square.x, square.y, square.side, square.side, 0, 0, PROFILE_PHOTO_SIDE, PROFILE_PHOTO_SIDE);
  for (const quality of [0.88, 0.8, 0.7, 0.6]) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    const base64 = base64Of(dataUrl);
    if (bytesOf(base64) <= PROFILE_PHOTO_MAX_BYTES) return { dataUrl, base64 };
  }
  throw new PhotoFileError("This photo has too much detail. Try another photo.");
}

/** The whole photo as one centred square, for a single upload with no face finder. */
export function centreSquare(source: Pick<PhotoSource, "width" | "height">): Square {
  const side = Math.min(source.width, source.height);
  return { x: Math.round((source.width - side) / 2), y: Math.round((source.height - side) / 2), side };
}
